import React, { useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import type { FormField, ThemeConfig } from '../../../types/forms';

interface YesNoFieldProps {
  field: FormField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const YesNoField: React.FC<YesNoFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  // Keyboard shortcuts: Y/S for yes, N for no
  useEffect(() => {
    if (disabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const key = e.key.toLowerCase();
      if (key === 'y' || key === 's') onChange?.('yes');
      if (key === 'n') onChange?.('no');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [disabled, onChange]);

  const options = [
    { key: 'yes', label: 'Sim', icon: ThumbsUp, shortcut: 'S' },
    { key: 'no', label: 'Não', icon: ThumbsDown, shortcut: 'N' },
  ];

  return (
    <div className="flex gap-3">
      {options.map((opt) => {
        const isSelected = value === opt.key;
        const Icon = opt.icon;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => !disabled && onChange?.(opt.key)}
            disabled={disabled}
            style={!isSelected && themeConfig?.textColor ? { color: themeConfig.textColor } : undefined}
            className={`
              flex-1 flex items-center justify-center gap-3 py-4 rounded-xl border-2
              font-medium transition-all duration-200
              ${isSelected
                ? 'bg-primary-500 border-primary-500 text-white'
                : `bg-neutral-900/50 ${error ? 'border-red-500' : 'border-neutral-700'} ${themeConfig?.textColor ? '' : 'text-neutral-300'} hover:border-neutral-500`
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            <Icon className="w-5 h-5" />
            {opt.label}
            <span className="text-xs opacity-50 ml-1">({opt.shortcut})</span>
          </button>
        );
      })}
    </div>
  );
};
